import type { IGroupedFilterConfig } from './configs';

type TSetFilters<TFilters> = (updater: (prev: TFilters) => TFilters) => void;

interface IRangeResetConfig<TFilters> {
  key: string,
  fromKey: keyof TFilters,
  toKey: keyof TFilters,
}

interface IDateResetConfig<TFilters> {
  key: string,
  fromKey: keyof TFilters,
  toKey: keyof TFilters,
}

interface ICreateRemoveFilterTagOptions<TFilters> {
  groupedConfigs: IGroupedFilterConfig<TFilters>[],
  rangeConfigs: IRangeResetConfig<TFilters>[],
  dateConfigs: IDateResetConfig<TFilters>[],
  booleanDefaults?: Record<string, unknown>,
  excludeReset?: Record<string, Partial<TFilters>>,
  removeSideEffects?: Record<string, string[]>,
}

const createRemoveFilterTag = <TFilters extends Record<string, unknown>>(
  {
    groupedConfigs,
    rangeConfigs,
    dateConfigs,
    booleanDefaults = {},
    excludeReset = {},
    removeSideEffects = {},
  }: ICreateRemoveFilterTagOptions<TFilters>,
) => (tagKey: string, setLocalFilters: TSetFilters<TFilters>): void => {
  const groupedConfig = groupedConfigs.find((c) => c.key === tagKey);
  if (groupedConfig) {
    setLocalFilters((prev) => ({
      ...prev,
      ...Object.fromEntries(groupedConfig.resetKeys.map((k) => [k, []])),
    }));
    return;
  }

  if (tagKey in excludeReset) {
    setLocalFilters((prev) => ({ ...prev, ...excludeReset[tagKey] }));
    return;
  }

  const rangeConfig = rangeConfigs.find((c) => c.key === tagKey);
  if (rangeConfig) {
    setLocalFilters((prev) => ({ ...prev, [rangeConfig.fromKey]: null, [rangeConfig.toKey]: null }));
    return;
  }

  const dateConfig = dateConfigs.find((c) => c.key === tagKey);
  if (dateConfig) {
    setLocalFilters((prev) => ({ ...prev, [dateConfig.fromKey]: null, [dateConfig.toKey]: null }));
    return;
  }

  if (tagKey in booleanDefaults) {
    setLocalFilters((prev) => ({ ...prev, [tagKey]: booleanDefaults[tagKey] }));
    return;
  }

  const sideEffectKeys = removeSideEffects[tagKey];
  const resets = sideEffectKeys
    ? Object.fromEntries([tagKey, ...sideEffectKeys].map((k) => [k, []]))
    : { [tagKey]: [] };
  setLocalFilters((prev) => ({ ...prev, ...resets }));
};

export default createRemoveFilterTag;
